import axios from 'axios';
import store from '../redux/store';
import {
  API_ADD_PAYMENT_CARD_CUSTOMER,
  API_ADD_PROMOTION,
  API_CHANGE_ORDER_TYPE,
  API_DELETE_CARD_CUSTOMER,
  API_DELETE_CUSTOMER_ACCOUNT,
  API_GET_GEOCODE_FROM_ADDRESS,
  API_GET_GEOCODE_FROM_PLACE_ID,
  API_GET_PLACES_AUTOCOMPLETE,
  API_UPDATE_PAYMENT_CARD_CUSTOMER,
  API_USER,
  APIE_CHECK_USER_EXISTENCE,
  WEBAPP,
} from '../constants';
import {
  getFirebaseCustomerId,
  getIdToken,
} from './firebase/firebaseAuthentication';
import { sendCloudWatchAlert } from '../utils/logs';
import { decrypt, handleJoiSchemaError } from '../utils';

const isDev = false;

export const DEFAULT_URL_DOMAIN = isDev
  ? 'http://localhost:3000'
  : 'https://dev-backend.dishop.co';

export const initializeBackend = (domain) => {
  // domain = 'https://dev-backend.dishop.co.ngrok.dev';
  // domain = 'http://localhost:3000';
  axios.defaults.baseURL = String(domain);
  axios.interceptors.request.use(
    async (config) => {
      try {
        const idToken = await getIdToken();
        if (idToken) {
          config.headers = {
            Authorization: `Bearer ${idToken}`,
          };
        }
      } catch (error) {
        console.warn('Could not fetch ID token:', error);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  return true;
};

export const sendPostRequest = async (url, data, addCompanyIdToBody = true) => {
  try {
    const { companyId } = store.getState().configurationReducer;
    const newData = addCompanyIdToBody ? { ...data, companyId } : data;
    return await axios({
      method: 'post',
      url,
      data: newData,
    });
  } catch (error) {
    throw error.response ? error.response.data.errorMessage : error;
  }
};

export const sendGetRequest = async (url, data, addCompanyIdParams = true) => {
  try {
    let newData = data;
    if (addCompanyIdParams) {
      const { companyId } = store.getState().configurationReducer;
      newData = { ...data, companyId };
    }
    return await axios({
      method: 'get',
      url,
      params: newData,
    });
  } catch (error) {
    throw error.response ? error.response.data.errorMessage : error;
  }
};

export const sendPutRequest = async (url, data) => {
  try {
    return await axios.put(url, data);
  } catch (error) {
    throw error.response ? error.response.data.errorMessage : error;
  }
};

export const sendDeleteRequest = async (url) => {
  try {
    return await axios.delete(url);
  } catch (error) {
    throw error.response ? error.response.data.errorMessage : error;
  }
};

export const addPromotionBackend = async (customerId, promoCode, deviceId) => {
  const { shopId } = store.getState().pendingOrderReducer;
  const data = {
    customerId,
    promoCode,
    deviceId,
    shopId,
    canal: WEBAPP,
  };
  return await sendPostRequest(API_ADD_PROMOTION, data);
};

export const createCustomerBackend = async (user) => {
  const { shopId } = store.getState().shopReducer;
  const data = {
    user,
    shopId,
  };
  const { companyId } = store.getState().configurationReducer;
  const url = API_USER(companyId, user.id);
  await sendPostRequest(url, data, false);
};

export const getCustomerBackend = async (customerId) => {
  try {
    if (customerId) {
      const { companyId } = store.getState().configurationReducer;
      const url = API_USER(companyId, customerId);
      const response = await sendGetRequest(url, {}, false);
      return response.data;
    }
  } catch (error) {
    sendCloudWatchAlert(
      `Could not get customer backend ${handleJoiSchemaError(error)}`
    );
  }
};

export const checkCustomerExistenceBackend = async (customerId) => {
  const { companyId } = store.getState().configurationReducer;
  const url = APIE_CHECK_USER_EXISTENCE(companyId, customerId);
  try {
    const response = await sendGetRequest(url, {}, false);
    return response.data;
  } catch (err) {
    return false;
  }
};

export const updateCustomerPaymentCardBackend = async (newCardId) => {
  const { user } = store.getState().userReducer;
  const data = {
    newCardId,
    customerId: user.customerId,
  };
  await sendPostRequest(API_UPDATE_PAYMENT_CARD_CUSTOMER, data);
};

export const addCustomerPaymentCardBackend = async (newCard) => {
  const { user } = store.getState().userReducer;
  const data = {
    newCard,
    customerId: user.customerId,
  };
  await sendPostRequest(API_ADD_PAYMENT_CARD_CUSTOMER, data);
};

export const deleteCustomerPaymentCardBackend = async (cardId) => {
  const { user } = store.getState().userReducer;
  const data = {
    cardId,
    customerId: user.customerId,
  };
  await sendPostRequest(API_DELETE_CARD_CUSTOMER, data);
};

export const deleteCustomerAccountBackend = async () => {
  const customerId = getFirebaseCustomerId();
  if (customerId) {
    sendCloudWatchAlert(`Delete user ${customerId}`);
    const { shopId } = store.getState().shopReducer;
    const data = {
      customerId,
      shopId,
    };
    await sendPostRequest(API_DELETE_CUSTOMER_ACCOUNT, data);
  }
};

export const updateUser = async (userId, updatedUserData) => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const url = API_USER(companyId, userId);
    const response = await sendPutRequest(url, updatedUserData);
    return response.data;
  } catch (error) {
    sendCloudWatchAlert(
      `Could not update user ${userId} of company ${companyId} ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const changeOrderTypeEndpoint = async (order, newOrderType) => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const data = { nature: newOrderType };
    const url = API_CHANGE_ORDER_TYPE(companyId, order.id, order.chargeId);
    const response = await sendPutRequest(url, data);
    return response.data;
  } catch (error) {
    sendCloudWatchAlert(
      `Could not changer order type from ${order.orderType
      } to ${newOrderType} of company ${companyId} ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const getPlacesAutocomplete = async (placeInput) => {
  try {
    const data = { placeInput };
    const { companyId } = store.getState().configurationReducer;
    const result = await sendGetRequest(
      API_GET_PLACES_AUTOCOMPLETE(companyId),
      data,
      false
    );
    return result.data;
  } catch (error) {
    sendCloudWatchAlert(
      `Could not get autocomplete for address ${placeInput} ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const getGeocodeByAddress = async (address) => {
  try {
    const data = { address };
    const { companyId } = store.getState().configurationReducer;
    const response = await sendGetRequest(
      API_GET_GEOCODE_FROM_ADDRESS(companyId),
      data,
      false
    );
    return response.data.results;
  } catch (error) {
    sendCloudWatchAlert(
      `Could not get geocode for address : ${address} ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const getGeocodeByPlaceId = async (placeId) => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const data = { placeId };
    const response = await sendGetRequest(
      API_GET_GEOCODE_FROM_PLACE_ID(companyId),
      data,
      false
    );
    return response.data.results;
  } catch (error) {
    sendCloudWatchAlert(
      `Could not fetch firebase credentials of company ${companyId} : ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const getCompanyFirebaseCredentials = async (companyId) => {
  const determineBaseUrl = () => {
    const windowUrl = window.location.origin;
    if (windowUrl?.includes('dev-webapp') || !windowUrl) {
      return `https://dev-backend.dishop.co`;
    }
    if (windowUrl?.includes('localhost')) {
      return 'https://dev-backend.dishop.co';
    }
    return `https://dev-backend.dishop.co`;
  };

  try {
    const baseUrl = determineBaseUrl();
    const encryptedFirebaseCredentials = await axios({
      method: 'get',
      url: `${baseUrl}/api/firebase/credentials`,
      params: {
        companyId,
      },
    });
    const decryptedFirebaseCredentials = await decrypt(
      encryptedFirebaseCredentials?.data
    );
    return decryptedFirebaseCredentials
      ? JSON.parse(decryptedFirebaseCredentials)
      : null;
  } catch (error) {
    sendCloudWatchAlert(
      `Could not fetch firebase credentials of company ${companyId} : ${error}`
    );
    throw error;
  }
};
